import moment from 'moment'
import React from 'react'

// Components
import formatInlineCode from '../../../utils/shortTextFormatting'
import BlogAd from '../../global/blogAds'
import BlogCollectionRow from '../../global/blogCollectionRow'
import Button from '../../global/button'
import Pagination from '../../global/pagination'
import SEOTags from '../../global/seoTags'
import SubscribeModal from '../../global/subscribeModal'
import GlobalContainer from '../globalContainer'
import Typography from '../typography'

// Styles
import {
  BlogListingContainer,
  BlogListingOuter,
  BlogPostItem,
  CollectionRow,
  PaginationContainer,
  SubscribeAd,
  SubscribeBanner,
} from './style'

// Helpers
import { OPTIONS } from '../../../constants/dateOptions'
import { generateBlogPostSlug, generateSecondaryBlogSlug } from '../../../utils/blog.utils'
import ImgUtil from '../../../utils/img.utils'
import { correctSlugPrefix } from '../../../utils/link.utils'
import WrapperImage from '../wrapperImage'

type BlogListingProps = {
  posts?: any
  slug: string
  seoData?: any
  totalPages: string
  pageNumber: string
  collectionRowData?: any
  subscribe?: any
  numListingPages: number
  blogFRData?: any
  locale: string
  disableListingTitle?: boolean
  listingTitle?: string
  isMainBlog?: boolean
  microcopyTextListingTitle?: string
}

const BlogListing = ({
  posts,
  slug,
  totalPages,
  pageNumber,
  seoData,
  collectionRowData,
  subscribe,
  numListingPages,
  blogFRData,
  locale,
  disableListingTitle,
  listingTitle,
  isMainBlog,
  microcopyTextListingTitle,
}: BlogListingProps) => {
  //////////////////////
  // Pagination
  // If blog featured resource is set, total listings per page should be 13 to accommadate for the featured resource slot
  const articlesPerPage = blogFRData ? 13 : 14
  const hrefForPage = (page: number) => {
    let url = ''

    const slugLink = correctSlugPrefix(slug)
    if (page === 1) {
      url = slugLink
    } else {
      url = `${slugLink}/${page}`
    }
    return url
  }

  //only used internally not by the paging system
  const handlePaginationClick = (page: number) => {
    if (page > 1) {
      history.pushState(null, '', `/${slug}/${page}`)
    } else {
      history.pushState(null, '', `/${slug}`)
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const currentPage = parseInt(pageNumber)
  // End Pagination
  //////////////////////

  // Featured resource
  const generateBlogFR = () => {
    if (blogFRData) {
      const { title, categoryNumber, category, image, imageAlt, ctaText, ctaUrl } = blogFRData
      return (
        <BlogAd
          ctaUrl={ctaUrl}
          ctaText={ctaText}
          adText={title}
          adImage={image}
          adImageAlt={imageAlt}
          className="BlogListing__Ad"
          key="BlogListing__Ad"
        />
      )
    } else {
      return null
    }
  }

  const getImageUrl = (item: any) =>
    item.image && item.image.file && item.image.file.url ? item.image.file.url : ''

  if (posts) {
    return (
      <BlogListingOuter $pageNumber={pageNumber} data-cy="BlogListing">
        <SEOTags
          seoMetadata={seoData}
          slug={slug}
          path={slug}
          isPaginated={true}
          pageNum={currentPage}
          hrefForPage={hrefForPage}
          isLastPage={currentPage === numListingPages}
        />
        <GlobalContainer>
          {currentPage === 1 && !disableListingTitle && (
            <>
              {listingTitle && (
                <Typography
                  className={`BlogListingContainer-Header ${
                    !isMainBlog ? 'BlogListingContainer-Header--SecondaryBlog' : ''
                  }`}
                  type="header3">
                  {listingTitle}
                </Typography>
              )}
              {!disableListingTitle && (
                <Typography className="BlogListingContainer-Header" type="header4">
                  {!microcopyTextListingTitle ? microcopyTextListingTitle : 'Latest Articles'}
                </Typography>
              )}
            </>
          )}
          <BlogListingContainer totalPost={posts.length}>
            {posts.map((post: any, i: number) => {
              const blogItem = post ? post : ''
              if (blogItem) {
                let categoryIcon = ''
                let categoryAltText = ''
                let blogOverwriteUrlText = ''
                if (blogItem.blogCategory && blogItem.blogCategory.overwriteUrlText)
                  blogOverwriteUrlText = blogItem.blogCategory.overwriteUrlText

                if (blogItem.category && blogItem.category.whiteImage) {
                  categoryIcon = getImageUrl(blogItem.category.whiteImage)
                  categoryAltText = blogItem.category.whiteImage.altText
                    ? blogItem.category.whiteImage.altText
                    : ''
                }

                const useCase = blogItem?.blogCategory?.useCase || ''
                let blogCategory =
                  blogItem.blogCategory && blogItem.blogCategory.title
                    ? blogItem.blogCategory.title
                    : ''
                let blogCategoryPath = blogCategory
                  .toLowerCase()
                  .replace(/ *\([^)]*\) */g, '')
                  .replace(/\s/g, '-')
                if (blogCategoryPath) {
                  if (blogCategoryPath === 'artificial-intelligence') {
                    blogCategoryPath = 'ai'
                  } else if (blogCategoryPath === 'robotic-process-automation') {
                    blogCategoryPath = 'rpa'
                  }
                }
                const blogDate = blogItem.date ? new Date(blogItem.date) : ''

                let convertedDate
                if (blogDate) {
                  convertedDate = moment.utc(blogDate).format('MMMM DD, YYYY') // 9 Jul 2020
                  convertedDate = new Date(convertedDate).toLocaleDateString(locale, OPTIONS)
                }
                const blogTitle = blogItem.h1Title ? blogItem.h1Title : ''
                let blogSubtitle = ''
                if (blogItem.teaserBody && blogItem.teaserBody.raw) {
                  blogSubtitle = blogItem.teaserBody.raw
                } else if (blogItem.seo && blogItem.seo.pageMetaDescription) {
                  blogSubtitle = blogItem.seo.pageMetaDescription
                }

                const blogImageURL =
                  blogItem.mainImage &&
                  blogItem.mainImage.image &&
                  blogItem.mainImage.image.file &&
                  blogItem.mainImage.image.file.url
                    ? blogItem.mainImage.image.file.url
                    : ''

                const blogImageAlt =
                  blogItem.mainImage && blogItem.mainImage.altText ? blogItem.mainImage.altText : ''

                let blogSlug = blogItem.slug ? blogItem.slug : '/blog'
                if (!isMainBlog) {
                  blogSlug = generateSecondaryBlogSlug({
                    slug: blogItem.slug,
                    blogCategoryName: blogOverwriteUrlText ? blogOverwriteUrlText : blogCategory,
                    useCase,
                    pageLocale: locale,
                  })
                } else {
                  blogSlug = generateBlogPostSlug({
                    slug: blogSlug,
                    blogCategoryName: blogOverwriteUrlText ? blogOverwriteUrlText : blogCategory,
                    pageLocale: locale,
                  })
                }

                const blogPost = (
                  <BlogPostItem href={blogSlug} key={`blogPostItem--${blogSlug}-${i}`}>
                    <div className="BlogPostItem-Img-Container">
                      {blogImageURL && (
                        <WrapperImage
                          className="BlogPostItem-Img"
                          src={ImgUtil.getResizedImageUrl(blogImageURL, 'width=484')}
                          alt={blogImageAlt}
                          width={384}
                          height={216}
                          threshold={650}
                        />
                      )}
                    </div>
                    <div className="BlogPostItem-Text">
                      <div className="BlogPostItem-Text-Category-Date">
                        {blogCategory && (
                          <Typography className="BlogPostItem-Text-Category" type="captionAlt">
                            {blogCategoryPath && ['ai', 'rpa'].includes(blogCategoryPath)
                              ? blogCategoryPath
                              : blogCategory}
                          </Typography>
                        )}
                        {convertedDate && (
                          <Typography className="BlogPostItem-Text-Date" type="caption">
                            {convertedDate}
                          </Typography>
                        )}
                      </div>
                      {blogTitle && (
                        <Typography className="BlogPostItem-Title" type="header6">
                          {blogTitle}
                        </Typography>
                      )}
                      {blogSubtitle && (
                        <Typography className="BlogPostItem-Subtitle" type="bodyXS">
                          {blogSubtitle}
                        </Typography>
                      )}
                    </div>
                  </BlogPostItem>
                )
                // Last item in listing
                if (i === posts.length - 1) {
                  return (
                    <React.Fragment key={`blogPostGroup--${i}`}>
                      {blogPost}
                      {subscribe && (
                        <SubscribeBanner key={`SubscribeBanner-${i}`}>
                          <div className="SubscribeBanner__Inner">
                            <Typography className="SubscribeBanner-Header" type="header5">
                              {subscribe.bannerHeadline}
                            </Typography>
                            <Button
                              icon={true}
                              text="Subscribe"
                              type="secondary"
                              size="small"
                              theme="dark"
                              className="SubscribeModal__Trigger"
                            />
                          </div>
                        </SubscribeBanner>
                      )}
                      {subscribe && (
                        <SubscribeAd key={`SubscribeAd-${i}`}>
                          <div className="SubscribeAd__Inner">
                            <div className="SubscribeAd__Img-Container">
                              <WrapperImage
                                className="SubscribeAd__Img"
                                src="https://images.ctfassets.net/5965pury2lcm/1hXSNS41cgsUwwJjwtL0zQ/e9f537c843a39ffa90bf50229bc60619/Icon-light-contact-email.svg"
                                alt="Mail Icon"
                                width={64}
                                height={64}
                                threshold={650}
                              />
                            </div>
                            <Typography className="SubscribeAd-Header" type="header5">
                              {subscribe.teaserHeadline}
                            </Typography>
                            <Typography className="SubscribeAd-Body" type="bodyXS">
                              {formatInlineCode(subscribe.teaserBody)}
                            </Typography>

                            <Button
                              icon={true}
                              text="Subscribe"
                              type="primary"
                              size="small"
                              theme="dark"
                              className="SubscribeModal__Trigger"
                            />
                          </div>
                        </SubscribeAd>
                      )}
                      {generateBlogFR()}
                    </React.Fragment>
                  )
                } else if (collectionRowData && i === 5 && parseInt(pageNumber) === 1) {
                  return (
                    <React.Fragment key={`blogPostGroup--${i}`}>
                      {blogPost && blogPost}
                      <CollectionRow>
                        <div className="CollectionRow__Background-Content"></div>
                        <BlogCollectionRow
                          className="BlogListing__Collection-Row"
                          headline={collectionRowData.headline || ''}
                          robotImage={
                            'https://images.ctfassets.net/5965pury2lcm/4XoMG9HzpeA1RK5OuXT40P/c609b68a4f50720181fcc37995eae6ab/Left.png'
                          }
                          categories={
                            collectionRowData.teasers
                              ? collectionRowData.teasers.map((item: any) => ({
                                  name: item.title || '',
                                  description: item.description || '',
                                  ctaText: 'Explore collection',
                                }))
                              : []
                          }
                        />
                      </CollectionRow>
                    </React.Fragment>
                  )
                } else {
                  return blogPost
                }
              }
            })}
          </BlogListingContainer>
          {parseInt(totalPages) / articlesPerPage > 1 && (
            <PaginationContainer>
              <Pagination
                activePage={currentPage}
                hrefForPage={hrefForPage}
                handleClick={handlePaginationClick}
                numResults={parseInt(totalPages)}
                numPagesBeforeEllipses={3}
                numResultsPerPage={articlesPerPage}
                numPages={numListingPages ? numListingPages : undefined}
              />
            </PaginationContainer>
          )}
        </GlobalContainer>
        {subscribe && <SubscribeModal data={subscribe} />}
      </BlogListingOuter>
    )
  } else {
    return null
  }
}

export default BlogListing
